import { FormikProvider, useFormik } from 'formik';
import { useCallback } from 'react';
import * as yup from 'yup';
import { queryClient } from '@/modules/common/query.ts';
import { preventFormDefault } from '@/modules/common/utils/preventFormDefault.ts';
import { ISSUER_REGISTER_NAV_QUERY_KEY } from '@/modules/issuer-dashboard/queries/nav.tsx';
import {
  ISSUER_REGISTER_CLASS_LIST_QUERY_KEY,
  useIssuerAddRegisterClassMutation,
} from '@/modules/issuer-dashboard/queries/registers.tsx';
import { Button, ModalContentWrapper, Select, SelectOption, TextInputField } from 'ui';
import { CreateRegisterClassRequest } from 'api';

export const validationSchema = yup.object({
  class_name: yup.string().trim().required('This field is required'),
  current_price_per_share: yup
    .number()
    .typeError('Price must be a number')
    .required('This field is required'),
  transfer_lock_period: yup.string().trim().nullable(),
  min_investment: yup
    .number()
    .typeError('Min investment must be a number')
    .required('This field is required')
    .when('current_price_per_share', ([currentPrice], schema) => {
      if (currentPrice == null) return schema;

      return schema.max(
        currentPrice,
        'Min investment cannot be greater than current price per share',
      );
    }),
});

const initialValues = {
  class_name: '',
  current_price_per_share: 1,
  transfer_lock_period: undefined,
  min_investment: 0.02,
};

export const transferLockPeriodOptions: SelectOption<number | null>[] = [
  { label: 'No transfer lock period', value: null },
  { label: '1 month', value: 30 },
  { label: '2 months', value: 2 * 30 },
  { label: '3 months', value: 3 * 30 },
  { label: '4 months', value: 4 * 30 },
  { label: '5 months', value: 5 * 30 },
  { label: '6 months', value: 6 * 30 },
  { label: '7 months', value: 7 * 30 },
  { label: '8 months', value: 8 * 30 },
  { label: '9 months', value: 9 * 30 },
  { label: '10 months', value: 10 * 30 },
  { label: '11 months', value: 11 * 30 },
  { label: '12 months', value: 12 * 30 },
  { label: '15 months', value: 15 * 30 },
  { label: '18 months', value: 18 * 30 },
  { label: '21 months', value: 21 * 30 },
  { label: '24 months', value: 24 * 30 },
  { label: '27 months', value: 27 * 30 },
  { label: '30 months', value: 30 * 30 },
  { label: '33 months', value: 33 * 30 },
  { label: '36 months', value: 36 * 30 },
  { label: '39 months', value: 39 * 30 },
  { label: '42 months', value: 42 * 30 },
  { label: '45 months', value: 45 * 30 },
  { label: '48 months', value: 48 * 30 },
  { label: '51 months', value: 51 * 30 },
  { label: '54 months', value: 54 * 30 },
  { label: '57 months', value: 57 * 30 },
  { label: '60 months', value: 60 * 30 },
];

export interface IssuerAddClassModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const IssuerAddClassModal = ({ open, onClose, onSuccess }: IssuerAddClassModalProps) => {
  const formik = useFormik<CreateRegisterClassRequest>({
    initialValues,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async values => {
      await addRegisterClass(values);
      await queryClient.invalidateQueries({ queryKey: [ISSUER_REGISTER_CLASS_LIST_QUERY_KEY] });
      await queryClient.invalidateQueries({ queryKey: [ISSUER_REGISTER_NAV_QUERY_KEY] });
      handleCloseModalWithResetForm();
    },
    enableReinitialize: true,
  });

  const { mutateAsync: addRegisterClass, isLoading } = useIssuerAddRegisterClassMutation({
    setErrors: formik.setErrors,
    onSuccess,
  });

  const handleCloseModalWithResetForm = useCallback(() => {
    formik.resetForm();
    onClose();
  }, [formik, onClose]);

  const handleChangeTransferLockPeriod = (date: number | null) => {
    formik.setFieldValue('transfer_lock_period', date || null);
  };
  return (
    <ModalContentWrapper open={open} title="Add new class" onClose={handleCloseModalWithResetForm}>
      <FormikProvider value={formik}>
        <form className="w-full flex flex-col space-y-10" onSubmit={preventFormDefault}>
          <div className="w-full flex flex-col space-y-8">
            <TextInputField name="class_name" label="class name" />
            <TextInputField
              name="current_price_per_share"
              label="Price per share/unit"
              type="number"
            />
            <TextInputField
              name="min_investment"
              label="Min. investment"
              type="number"
              min="0"
              step="0.01"
            />
            <Select<number | null>
              label="transfer lock period"
              placeholder="No transfer lock period"
              options={transferLockPeriodOptions}
              value={formik.values.transfer_lock_period}
              onChange={handleChangeTransferLockPeriod}
            />
          </div>
          <Button onClick={formik.submitForm} loading={isLoading}>
            Create class
          </Button>
        </form>
      </FormikProvider>
    </ModalContentWrapper>
  );
};
